import moment, { Moment } from 'moment-timezone';
import {
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterSelections,
  PricingLastInvoiceFilterValue,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';

const buildPillMatcher = <T>(
  section: Extract<
    PricingFilterSection<T>,
    { kind: PricingFilterSectionKind.Pill }
  >,
  selected: string[] | undefined,
): ((row: T) => boolean) | null => {
  if (!selected || selected.length === 0) {
    return null;
  }

  const selectedSet = new Set(selected);
  return (row) =>
    section.getValues(row).some((value) => selectedSet.has(value));
};

const buildRangeMatcher = <T>(
  section: Extract<
    PricingFilterSection<T>,
    { kind: PricingFilterSectionKind.Range }
  >,
  bound: { max: number | null; min: number | null } | undefined,
): ((row: T) => boolean) | null => {
  if (!bound || (bound.min === null && bound.max === null)) {
    return null;
  }

  return (row) => {
    const value = section.getValue(row);
    if (value === null) {
      return false;
    }
    if (bound.min !== null && value < bound.min) {
      return false;
    }
    if (bound.max !== null && value > bound.max) {
      return false;
    }
    return true;
  };
};

// Bounds are UTC-anchored calendar days; re-anchor the row's date the same way.
const toUtcDayValue = (date: Moment) =>
  moment.utc([date.year(), date.month(), date.date()]).valueOf();

const buildDateRangeMatcher = <T>(
  section: Extract<
    PricingFilterSection<T>,
    { kind: PricingFilterSectionKind.DateRange }
  >,
  selectedBucket: string[] | undefined,
  customBound: { max: number | null; min: number | null } | undefined,
): ((row: T) => boolean) | null => {
  const selected = selectedBucket?.[0];
  if (!selected) {
    return null;
  }

  if (selected === PricingLastInvoiceFilterValue.Never) {
    return (row) => section.getDate(row) === null;
  }

  if (selected === PricingLastInvoiceFilterValue.Recent) {
    const threshold = moment().subtract(7, 'days');
    return (row) => {
      const date = section.getDate(row);
      return date !== null && date.isSameOrAfter(threshold);
    };
  }

  // Custom: inert until both bounds of the picked range are set.
  if (!customBound || (customBound.min === null && customBound.max === null)) {
    return null;
  }

  return (row) => {
    const date = section.getDate(row);
    if (date === null) {
      return false;
    }
    const rowDay = toUtcDayValue(date);
    if (customBound.min !== null && rowDay < customBound.min) {
      return false;
    }
    if (customBound.max !== null && rowDay > customBound.max) {
      return false;
    }
    return true;
  };
};

export const buildPricingFilterPredicate = <T>(
  filters: PricingFilterSelections,
  sections: PricingFilterSection<T>[],
): ((row: T) => boolean) => {
  const matchers = sections.reduce<((row: T) => boolean)[]>((acc, section) => {
    const matcher =
      section.kind === PricingFilterSectionKind.Pill
        ? buildPillMatcher(section, filters.pills[section.id])
        : section.kind === PricingFilterSectionKind.Range
          ? buildRangeMatcher(section, filters.ranges[section.id])
          : buildDateRangeMatcher(
              section,
              filters.pills[section.id],
              filters.ranges[section.id],
            );

    if (matcher) {
      acc.push(matcher);
    }
    return acc;
  }, []);

  return (row) => matchers.every((matches) => matches(row));
};

export const applyPricingFilters = <T>(
  rows: T[],
  filters: PricingFilterSelections,
  sections: PricingFilterSection<T>[],
): T[] => {
  // Skip the allocation entirely when nothing is active, so callers relying
  // on referential identity (e.g. to avoid re-rendering) still get it.
  if (!sections.some((section) => isSectionActive(section, filters))) {
    return rows;
  }

  return rows.filter(buildPricingFilterPredicate(filters, sections));
};

export const isSectionActive = <T>(
  section: PricingFilterSection<T>,
  filters: PricingFilterSelections,
): boolean => {
  if (section.kind === PricingFilterSectionKind.Pill) {
    return (filters.pills[section.id]?.length ?? 0) > 0;
  }

  if (section.kind === PricingFilterSectionKind.DateRange) {
    const selected = filters.pills[section.id]?.[0];
    if (!selected) {
      return false;
    }
    if (selected !== PricingLastInvoiceFilterValue.Custom) {
      return true;
    }
    const bound = filters.ranges[section.id];
    return Boolean(bound && (bound.min !== null || bound.max !== null));
  }

  const bound = filters.ranges[section.id];
  return Boolean(bound && (bound.min !== null || bound.max !== null));
};

export const countActiveFilterSections = <T>(
  filters: PricingFilterSelections,
  sections: PricingFilterSection<T>[],
): number =>
  sections.filter((section) => isSectionActive(section, filters)).length;
